import React, { useState, useEffect, createContext, useContext } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { 
  Sparkles, 
  LogOut, 
  Settings, 
  Plus,
  ChevronDown,
  Bot,
  X,
  Menu,
  Home,
  FolderKanban,
  HelpCircle,
  Bell,
  Target
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/lib/api';
import { toast } from 'sonner';
import { PillButton } from '@/components/ios26/IOSComponents';
import DiagnosticsPanel from '@/components/ios26/DiagnosticsPanel';
import '@/styles/ios26-design-system.css';

/**
 * App Shell Context - Manages global app state
 */
const AppShellContext = createContext(null);

export const useAppShell = () => {
  const context = useContext(AppShellContext);
  if (!context) {
    throw new Error('useAppShell must be used within AppShellProvider');
  }
  return context;
};

/**
 * AppShellLayout - iOS 26-Inspired Main Layout
 * =============================================
 * Used by /dashboard and all authenticated pages
 * - Frosted glass top bar
 * - Project switcher
 * - User menu
 * - Persistent diagnostics panel (collapsible)
 * USES SEMANTIC TOKENS for dark mode compatibility
 */
export default function AppShellLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  
  const [projects, setProjects] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showProjectMenu, setShowProjectMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Load projects
  useEffect(() => {
    loadProjects();
  }, []);
  
  // Detect current project from URL
  useEffect(() => {
    const match = location.pathname.match(/\/projects\/([^/]+)/);
    if (match && projects.length > 0) {
      const project = projects.find(p => p.id === match[1] || p.project_id === match[1]);
      setCurrentProject(project);
    }
  }, [location.pathname, projects]);
  
  const loadProjects = async () => {
    try {
      const response = await apiClient.get('/projects');
      setProjects(response.data || []);
    } catch (error) {
      console.error('Failed to load projects:', error);
    }
  };
  
  const handleLogout = () => {
    logout();
    navigate('/auth');
  };
  
  const handleProjectSwitch = (project) => {
    setCurrentProject(project);
    setShowProjectMenu(false);
    // P0 FIX: Always go to calendar (new UI), not guided setup
    navigate(`/projects/${project.id || project.project_id}/calendar`);
  };
  
  const handleNewProject = () => {
    setShowProjectMenu(false);
    navigate('/projects/new');
  };
  
  return (
    <AppShellContext.Provider value={{
      projects,
      currentProject,
      setCurrentProject,
      showDiagnostics,
      setShowDiagnostics,
      refreshProjects: loadProjects
    }}>
      <div className="min-h-screen ios-bg-mesh">
        {/* ============================================
            TOP NAVIGATION BAR
            ============================================ */}
        <header 
          className="sticky top-0 z-50 ios-glass"
          style={{ borderBottom: '1px solid hsl(var(--color-border) / 0.5)' }}
        >
          <div className="max-w-[1800px] mx-auto px-4 lg:px-6">
            <div className="flex items-center justify-between h-16">
              {/* Left: Logo + Mobile Menu */}
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="lg:hidden p-2 rounded-xl"
                  style={{ backgroundColor: 'transparent' }}
                >
                  <Menu className="w-5 h-5 text-semantic" />
                </button>
                
                <button 
                  onClick={() => navigate('/dashboard')}
                  className="flex items-center gap-3"
                >
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <span className="hidden sm:block text-lg font-bold text-semantic">
                    AI Marketing Engine
                  </span>
                </button>
              </div>
              
              {/* Center: Project Switcher */}
              <div className="hidden md:block relative">
                <button
                  onClick={() => setShowProjectMenu(!showProjectMenu)}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl transition-colors"
                  style={{
                    backgroundColor: 'hsl(var(--color-surface))',
                    border: '1px solid hsl(var(--color-border))'
                  }}
                >
                  <FolderKanban className="w-4 h-4 text-semantic-muted" />
                  <span className="text-sm font-medium text-semantic-2 max-w-[200px] truncate">
                    {currentProject?.name || 'Sélectionner un projet'}
                  </span>
                  <ChevronDown className={cn(
                    'w-4 h-4 text-semantic-muted transition-transform',
                    showProjectMenu && 'rotate-180'
                  )} />
                </button>
                
                {/* Project Dropdown */}
                {showProjectMenu && (
                  <>
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setShowProjectMenu(false)} 
                    />
                    <div 
                      className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-72 rounded-2xl shadow-xl overflow-hidden z-50"
                      style={{
                        backgroundColor: 'hsl(var(--color-surface))',
                        border: '1px solid hsl(var(--color-border))'
                      }}
                    >
                      <div className="p-2" style={{ borderBottom: '1px solid hsl(var(--color-border-subtle))' }}>
                        <p className="px-3 py-1.5 text-xs font-semibold text-semantic-muted uppercase">
                          Mes projets
                        </p>
                      </div>
                      <div className="max-h-64 overflow-y-auto p-2">
                        {projects.length === 0 ? (
                          <p className="px-3 py-4 text-sm text-semantic-muted text-center">
                            Aucun projet
                          </p>
                        ) : (
                          projects.map((project) => (
                            <button
                              key={project.id || project.project_id}
                              onClick={() => handleProjectSwitch(project)}
                              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors"
                              style={{
                                backgroundColor: (currentProject?.id === project.id || currentProject?.project_id === project.project_id)
                                  ? 'hsl(var(--sidebar-item-active-bg))'
                                  : 'transparent',
                                color: (currentProject?.id === project.id || currentProject?.project_id === project.project_id)
                                  ? 'hsl(var(--sidebar-text-active))'
                                  : 'inherit'
                              }}
                            >
                              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-sm font-bold">
                                {project.name?.charAt(0)?.toUpperCase() || 'P'}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm truncate text-semantic">{project.name}</p>
                                <p className="text-xs text-semantic-muted truncate">{project.website_url || 'Pas de site'}</p>
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                      <div className="p-2" style={{ borderTop: '1px solid hsl(var(--color-border-subtle))' }}>
                        <button
                          onClick={handleNewProject}
                          className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl font-medium text-sm transition-colors"
                          style={{
                            backgroundColor: 'hsl(var(--color-primary))',
                            color: 'hsl(var(--color-primary-text))'
                          }}
                        >
                          <Plus className="w-4 h-4" />
                          Nouveau projet
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
              
              {/* Right: Actions */}
              <div className="flex items-center gap-2">
                {/* P0 CRM: Prospecting Agent Quick Access */}
                <button
                  onClick={() => navigate('/prospecting')}
                  className="flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-all"
                  style={{
                    backgroundColor: location.pathname === '/prospecting'
                      ? 'hsl(142, 70%, 45%)'
                      : 'hsl(var(--color-surface-2))',
                    color: location.pathname === '/prospecting'
                      ? '#ffffff'
                      : 'hsl(var(--color-text-2))',
                    boxShadow: location.pathname === '/prospecting'
                      ? '0 4px 12px hsl(142 70% 45% / 0.25)'
                      : 'none'
                  }}
                  data-testid="nav-prospecting-btn"
                  title="Agent de Prospection CRM"
                >
                  <Target className="w-4 h-4" />
                  <span className="hidden sm:inline">Prospection</span>
                </button>

                {/* AI Assistant Toggle */}
                <button
                  onClick={() => setShowDiagnostics(!showDiagnostics)}
                  className="flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-all"
                  style={{
                    backgroundColor: showDiagnostics 
                      ? 'hsl(var(--color-primary))' 
                      : 'hsl(var(--color-surface-2))',
                    color: showDiagnostics 
                      ? 'hsl(var(--color-primary-text))' 
                      : 'hsl(var(--color-text-2))',
                    boxShadow: showDiagnostics ? '0 4px 12px hsl(var(--color-primary) / 0.25)' : 'none'
                  }}
                >
                  <Bot className="w-4 h-4" />
                  <span className="hidden sm:inline">Assistant</span>
                </button>
                
                {/* User Menu */}
                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center gap-2 p-2 rounded-xl transition-colors"
                    style={{ backgroundColor: 'transparent' }}
                  >
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-600 to-gray-800 flex items-center justify-center text-white text-sm font-bold">
                      {user?.email?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <ChevronDown className="w-4 h-4 text-semantic-muted hidden sm:block" />
                  </button>
                  
                  {/* User Dropdown */}
                  {showUserMenu && (
                    <>
                      <div 
                        className="fixed inset-0 z-40" 
                        onClick={() => setShowUserMenu(false)} 
                      />
                      <div 
                        className="absolute top-full right-0 mt-2 w-56 rounded-2xl shadow-xl overflow-hidden z-50"
                        style={{
                          backgroundColor: 'hsl(var(--color-surface))',
                          border: '1px solid hsl(var(--color-border))'
                        }}
                      >
                        <div className="p-4" style={{ borderBottom: '1px solid hsl(var(--color-border-subtle))' }}>
                          <p className="font-medium text-semantic truncate">{user?.email}</p>
                          <p className="text-xs text-semantic-muted capitalize">{user?.plan_tier || 'Free'}</p>
                        </div>
                        <div className="p-2">
                          <button
                            onClick={() => {
                              setShowUserMenu(false);
                              navigate('/settings/support/diagnostics');
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors"
                            style={{ backgroundColor: 'transparent' }}
                          >
                            <HelpCircle className="w-4 h-4 text-semantic-muted" />
                            <span className="text-sm text-semantic">Support & Diagnostics</span>
                          </button>
                          <button
                            onClick={() => {
                              setShowUserMenu(false);
                              handleLogout();
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors"
                            style={{ 
                              backgroundColor: 'transparent',
                              color: 'hsl(var(--color-danger))'
                            }}
                          >
                            <LogOut className="w-4 h-4" />
                            <span className="text-sm">Déconnexion</span>
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>
        
        {/* ============================================
            MAIN CONTENT AREA
            ============================================ */}
        <main className={cn(
          'transition-all duration-300',
          showDiagnostics && 'lg:mr-96'
        )}>
          {children}
        </main>
        
        {/* ============================================
            DIAGNOSTICS PANEL (Right Side)
            ============================================ */}
        {showDiagnostics && (
          <DiagnosticsPanel 
            onClose={() => setShowDiagnostics(false)}
            projectId={currentProject?.id || currentProject?.project_id}
          />
        )}
        
        {/* Mobile Menu Overlay */}
        {mobileMenuOpen && (
          <>
            <div 
              className="fixed inset-0 bg-black/30 z-40 lg:hidden"
              onClick={() => setMobileMenuOpen(false)}
            />
            <div 
              className="fixed left-0 top-0 bottom-0 w-72 z-50 lg:hidden ios-animate-slide-in"
              style={{ backgroundColor: 'hsl(var(--color-surface))' }}
            >
              <div 
                className="p-4 flex items-center justify-between"
                style={{ borderBottom: '1px solid hsl(var(--color-border-subtle))' }}
              >
                <span className="font-bold text-semantic">Menu</span>
                <button 
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 rounded-lg"
                  style={{ backgroundColor: 'hsl(var(--color-surface-2))' }}
                >
                  <X className="w-5 h-5 text-semantic" />
                </button>
              </div>
              <nav className="p-4 space-y-2">
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    navigate('/dashboard');
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl"
                  style={{ backgroundColor: 'transparent' }}
                >
                  <Home className="w-5 h-5 text-semantic-muted" />
                  <span className="text-semantic">Tableau de bord</span>
                </button>

                {/* P0 CRM: Prospecting Agent in mobile menu */}
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    navigate('/prospecting');
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl"
                  style={{ backgroundColor: 'transparent' }}
                  data-testid="mobile-nav-prospecting"
                >
                  <Target className="w-5 h-5" style={{ color: 'hsl(142, 70%, 45%)' }} />
                  <span className="text-semantic">Agent de Prospection</span>
                </button>
                
                <div className="pt-4" style={{ borderTop: '1px solid hsl(var(--color-border-subtle))' }}>
                  <p className="px-4 py-2 text-xs font-semibold text-semantic-muted uppercase">Projets</p>
                  {projects.map((project) => (
                    <button
                      key={project.id || project.project_id}
                      onClick={() => {
                        setMobileMenuOpen(false);
                        handleProjectSwitch(project);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl"
                      style={{ backgroundColor: 'transparent' }}
                    >
                      <div className="w-6 h-6 rounded-md bg-blue-500 text-white text-xs font-bold flex items-center justify-center">
                        {project.name?.charAt(0)?.toUpperCase()}
                      </div>
                      <span className="text-sm truncate text-semantic">{project.name}</span>
                    </button>
                  ))}
                </div>
                
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    navigate('/projects/new');
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl"
                  style={{
                    backgroundColor: 'hsl(var(--color-info-bg))',
                    color: 'hsl(var(--color-primary))'
                  }}
                >
                  <Plus className="w-5 h-5" />
                  <span>Nouveau projet</span>
                </button>
              </nav>
            </div>
          </>
        )}
      </div>
    </AppShellContext.Provider>
  );
}

export { AppShellContext };
